import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { getConversationList } from '@api/resident';
import { Conversation } from '@typings/ResidentTypes';
import { ApiResponse, ListApiResponse } from '@typings/ApiResponse';
import { truncateEmail } from '@utils/commons';
import { NextOfKinDetails } from './HavingConversationForm';
import Pagination from '../../../pagination';

interface ConversationLogProps {
  slug: string | undefined;
  formSubmitted: boolean;
  nextOfKinDetails: NextOfKinDetails;
}

const ConversationLog: React.FC<ConversationLogProps> = ({
  slug,
  nextOfKinDetails,
  formSubmitted,
}) => {
  const [conversationList, setConversationList] = useState<Conversation[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (slug) {
      const handleGetConversations = async () => {
        try {
          const data: ApiResponse<ListApiResponse<Conversation>> =
            await getConversationList(currentPage, 5, slug);
          if (data && data.status === 'success') {
            setConversationList(data?.data?.data);
            setTotalPages(data?.data?.pages);
          }
        } catch (error) {
          console.error(error);
        }
      };
      handleGetConversations();
    }
  }, [slug, formSubmitted, currentPage]);

  const checkIsNOK = (conversation: Conversation) => {
    if (
      nextOfKinDetails.fullName === conversation.fullName &&
      nextOfKinDetails.phoneNumber === conversation.phoneNumber &&
      nextOfKinDetails.relationship.toLowerCase() ===
        conversation.relationship.toLowerCase()
    ) {
      return 'Yes';
    }
    return 'No';
  };

  const showDiscussStatus = (status: boolean | string) => {
    if (status === '') {
      return ' ';
    }
    if (status === true) {
      return 'Yes';
    }
    if (status === false) {
      return 'No';
    }
    return ' ';
  };

  return (
    <div className="mt-10 govuk-bp:mt-14">
      <h1 className="govuk-heading-l">Conversation Log</h1>
      <p className="govuk-body">
        Each time an Advance Care Plan conversation is added in the form above
        it will be displayed in the list below. The list is provided in reverse
        chronological order with the latest logged conversation at the top.
      </p>

      <div className="overflow-x-auto mt-10 govuk-bp:mt-14">
        <table className="govuk-table">
          <thead className="govuk-table__head">
            <tr className="govuk-table__row">
              <th scope="col" className="govuk-table__header">
                Name
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-text-align-centre"
              >
                Email
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-text-align-centre"
              >
                Phone
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-text-align-centre govuk-!-text-align-centre"
              >
                Relationship
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-text-align-centre"
              >
                Allowed to discuss
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-text-align-centre"
              >
                Next of Kin
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-text-align-centre"
              >
                Date
              </th>
            </tr>
          </thead>
          <tbody className="govuk-table__body">
            {conversationList?.map(conversation => {
              return (
                <tr className="govuk-table__row" key={conversation.id}>
                  <td className="govuk-table__cell">{conversation.fullName}</td>
                  <td
                    className="govuk-table__cell govuk-!-padding-right-2 govuk-!-text-align-centre"
                    title={conversation.email}
                  >
                    {truncateEmail(conversation.email)}
                  </td>
                  <td className="govuk-table__cell govuk-!-padding-right-2 govuk-!-text-align-centre">
                    {conversation.phoneNumber}
                  </td>
                  <td className="govuk-table__cell govuk-!-padding-right-2 govuk-!-text-align-centre">
                    {conversation.relationship}
                  </td>

                  <td className="govuk-table__cell govuk-!-padding-right-2 govuk-!-text-align-centre">
                    <strong
                      className={`govuk-tag ${conversation.isAllowed ? 'govuk-tag--green' : 'govuk-tag--red'}`}
                    >
                      {showDiscussStatus(conversation.isAllowed)}
                    </strong>
                  </td>
                  <td className="govuk-table__cell govuk-!-padding-right-2 govuk-!-text-align-centre">
                    <strong
                      className={`govuk-tag ${conversation.nextOfKin ? 'govuk-tag--green' : 'govuk-tag--red'}`}
                    >
                      {conversation.nextOfKin ? 'Yes' : 'No'}
                    </strong>
                  </td>
                  <td className="govuk-table__cell govuk-!-padding-right-2 govuk-!-text-align-centre">
                    {dayjs(conversation.createdAt).format('DD/MM/YYYY')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex justify-end">
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default ConversationLog;
